/**
 * UndoToast — 5-second undo notification for soft-deleted entries.
 *
 * Appears at the bottom of the screen with an "Undo" button that calls
 * the restore function (POST /me/entries/:id/restore). Auto-dismisses
 * after 5 seconds per 05-ux-standards.md.
 *
 * Accessibility: role="status" + aria-live="polite" announces to screen
 * readers. The Undo button is keyboard-focusable and 44px tall.
 */
import { useEffect, useRef } from 'react';

interface UndoToastProps {
  message: string;
  onUndo: () => void;
  onDismiss: () => void;
  /** Countdown in ms; defaults to 5000. */
  durationMs?: number;
}

export function UndoToast({ message, onUndo, onDismiss, durationMs = 5000 }: UndoToastProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, durationMs);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [onDismiss, durationMs]);

  function handleUndo() {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    onUndo();
    onDismiss();
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="
        fixed bottom-6 left-1/2 z-50 -translate-x-1/2
        flex items-center gap-3
        rounded-lg border border-husk bg-shell px-4 py-3 shadow-lg
        text-sm text-ink
        dark:border-ink/20 dark:bg-leaf dark:text-ink
        animate-in slide-in-from-bottom-4 duration-150
      "
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={handleUndo}
        className="
          font-medium text-ink underline underline-offset-2
          hover:no-underline focus:outline-none focus:ring-2 focus:ring-leaf
          dark:text-ink
          min-h-[44px] px-1
        "
        aria-label="Undo delete"
      >
        Undo
      </button>
    </div>
  );
}
